import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { CTA_BY_TYPE, ctaStyle, ctaHref, groupStories, groupRingStyle } from "../data/stories";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
// Super-optimised delivery: storage images are served resized as WebP via the
// /files proxy (?w=&q=) with a 1-year immutable cache. External http URLs pass through.
const mediaUrl = (u, w) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  const base = `${API}/files/${u}`;
  return w ? `${base}?w=${w}&q=68` : base;
};

const SEEN_KEY = (pid) => `stories_seen_${pid}`;
export const getSeen = (pid) => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY(pid)) || "[]")); } catch { return new Set(); }
};
const addSeen = (pid, id) => {
  try {
    const s = getSeen(pid); s.add(id);
    localStorage.setItem(SEEN_KEY(pid), JSON.stringify([...s]));
  } catch { /* ignore */ }
};

// ─── Preview circles row (entry point in Esplora) ───────────────────────
// Instagram-style: each dynamic label = ONE bubble grouping all its stories.
export const StoriesRow = ({ stories, lang, propertyId, onOpen, seenVersion }) => {
  if (!stories || stories.length === 0) return null;
  const seen = getSeen(propertyId);
  const groups = groupStories(stories, lang);
  return (
    <div className="-mx-6 px-6 overflow-x-auto no-scrollbar" data-testid="stories-row">
      <div className="flex gap-4 pb-1">
        {groups.map((g, i) => {
          const allSeen = g.stories.every((s) => seen.has(s.story_id));
          const cover = g.cover;
          return (
            <button key={g.label} onClick={() => onOpen(i)} className="flex flex-col items-center gap-1.5 shrink-0 outline-none" data-testid={`story-circle-${i}`}>
              <motion.div
                layoutId={`hero-${cover.story_id}`}
                className="w-[88px] h-[88px] rounded-full p-[3px]"
                style={{ background: groupRingStyle(g, allSeen) }}
                animate={allSeen ? {} : { scale: [1, 1.05, 1] }}
                transition={allSeen ? {} : { duration: 2.6, repeat: Infinity, ease: "easeInOut", delay: (i % 4) * 0.4 }}
              >
                <div className="w-full h-full rounded-full overflow-hidden bg-white p-[2px] relative">
                  <img src={mediaUrl(cover.media_url, 240)} alt="" className="w-full h-full rounded-full object-cover" loading="lazy" decoding="async" />
                  {g.stories.length > 1 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-[20px] px-1 rounded-full bg-zinc-900 text-white text-[10px] font-black flex items-center justify-center border-2 border-white shadow-sm" data-testid={`story-count-${i}`}>
                      {g.stories.length}
                    </span>
                  )}
                </div>
              </motion.div>
              <span className="text-[11px] font-bold text-zinc-600 leading-none max-w-[92px] truncate">{g.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Fullscreen stories viewer ──────────────────────────────────────────
export const StoriesViewer = ({ stories, startIndex, originId, lang, propertyId, onClose, onSeen }) => {
  const [index, setIndex] = useState(startIndex || 0);
  const [progress, setProgress] = useState(0);
  const pausedRef = useRef(false);
  const rafRef = useRef(null);
  const startRef = useRef(0);
  const DURATION = 5500;

  const story = stories[index];

  const markSeen = useCallback((i) => {
    const s = stories[i];
    if (s) { addSeen(propertyId, s.story_id); onSeen && onSeen(); }
  }, [stories, propertyId, onSeen]);

  const next = () => {
    if (index >= stories.length - 1) onClose();
    else setIndex((i) => i + 1);
  };

  const prev = () => setIndex((i) => Math.max(0, i - 1));

  // Auto-advance timer (rAF based, pausable)
  useEffect(() => {
    markSeen(index);
    setProgress(0);
    startRef.current = performance.now();
    // Preload the next story image so swiping forward is instant.
    const nxt = stories[index + 1];
    if (nxt) { const im = new Image(); im.src = mediaUrl(nxt.media_url, 1080); }
    const tick = (now) => {
      if (!pausedRef.current) {
        const elapsed = now - startRef.current;
        const p = Math.min(100, (elapsed / DURATION) * 100);
        setProgress(p);
        if (p >= 100) { next(); return; }
      } else {
        startRef.current = now - (progress / 100) * DURATION;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [index]); // eslint-disable-line

  const onTap = (e) => {
    const x = e.clientX;
    const w = window.innerWidth;
    if (x < w * 0.32) prev();
    else next();
  };

  // Swipe down to close
  const dragStart = useRef(0);

  if (!story) return null;
  const heroLayout = story.story_id === originId;
  const buttons = (story.cta_buttons || []).filter((b) => b.value);

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="stories-viewer"
    >
      {/* Media */}
      <div className="absolute inset-0" onClick={onTap}>
        <motion.div
          {...(heroLayout ? { layoutId: `hero-${story.story_id}` } : {})}
          className="absolute inset-0 overflow-hidden"
        >
          <motion.img
            key={index}
            src={mediaUrl(story.media_url, 1080)}
            alt={story.title}
            className="w-full h-full object-cover"
            initial={{ scale: 1 }}
            animate={{ scale: 1.08 }}
            transition={{ duration: 6, ease: "linear" }}
            draggable={false}
            decoding="async"
          />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg,rgba(0,0,0,0.45) 0%,rgba(0,0,0,0) 22%,rgba(0,0,0,0) 55%,rgba(0,0,0,0.75) 100%)" }} />
        </motion.div>
      </div>

      {/* Progress bars */}
      <div className="absolute top-0 left-0 right-0 flex gap-1 p-3 pt-4 z-20">
        {stories.map((_, i) => (
          <div key={i} className="flex-1 h-[3px] rounded-full bg-white/30 overflow-hidden">
            <div className="h-full bg-white rounded-full" style={{ width: i < index ? "100%" : i === index ? `${progress}%` : "0%" }} />
          </div>
        ))}
      </div>

      {/* Close */}
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-5 right-3 z-30 p-2 rounded-full bg-black/30 backdrop-blur-sm" data-testid="stories-close">
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Hold to pause (covers center, above tap zones? no — use pointer events on wrapper) */}
      <div
        className="absolute inset-0 z-10"
        onClick={onTap}
        onPointerDown={(e) => { pausedRef.current = true; dragStart.current = e.clientY; }}
        onPointerUp={(e) => {
          pausedRef.current = false;
          if (e.clientY - dragStart.current > 90) onClose();
        }}
        onPointerCancel={() => { pausedRef.current = false; }}
      />

      {/* Overlay text + CTA */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-5 pb-7 pointer-events-none">
        {story.source === "community" && (
          <span className="inline-block mb-2 text-[10px] font-black uppercase tracking-wider text-white/90 bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full">
            {lang === "en" ? "Recommended by the community" : lang === "fr" ? "Recommandé par la communauté" : lang === "es" ? "Recomendado por la comunidad" : lang === "de" ? "Von der Community empfohlen" : "Consigliato dalla community"}
          </span>
        )}
        <h3 className="text-white text-[24px] font-black tracking-tight leading-tight drop-shadow-lg">{story.title}</h3>
        {story.description && <p className="text-white/90 text-[14px] font-medium leading-snug mt-1 drop-shadow-md max-w-[90%]">{story.description}</p>}
        {buttons.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pointer-events-auto">
            {buttons.map((b, i) => {
              const meta = CTA_BY_TYPE[b.type];
              const Icon = meta?.icon;
              const st = ctaStyle(b.type);
              return (
                <a
                  key={i}
                  href={ctaHref(b)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 text-[13px] font-black px-4 py-2.5 rounded-full shadow-lg active:scale-[0.97] transition-transform"
                  style={st}
                  data-testid={`story-cta-${b.type}`}
                >
                  {Icon && <Icon className="w-4 h-4" strokeWidth={2.4} />}
                  {(meta?.label || {})[lang] || (meta?.label || {}).it || b.type}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};
